import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getDatalists, setDatalists } from 'features/datalist/datalist.slice';
import {
    useUpdateSpecificDataMutation,
    useDeleteSpecificDataMutation,
    useGetAllDatasMutation,
    useCreateSpecificDataMutation
} from 'services/data/data.endpoint';
import { useApiToast } from 'components/Toaster';

export const useDataList = () => {
    const dispatch = useDispatch();
    const { datalists } = useSelector(getDatalists);
    const [dataListItemToEdit, setDataListItemToEdit] = useState<{ id: number; label: string } | null>(null);
    const [dataListItemToDelete, setDataListItemToDelete] = useState<{ id: number; label: string } | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [currentListType, setCurrentListType] = useState<string>('');
    const [modalMode, setModalMode] = useState<'edit' | 'delete' | 'create'>('edit');
    const { showApiToast, showErrorToast } = useApiToast();
    const [lastCreatedItemLabel, setLastCreatedItemLabel] = useState<string>('');
    const [lastUpdatedItemLabel, setLastUpdatedItemLabel] = useState<string>('');
    const [lastDeletedItemLabel, setLastDeletedItemLabel] = useState<string>('');   

    const [
        getAllDatas,
        {
            isLoading: isGetAllDatasLoading,
            isError: isGetAllDatasError,
            isSuccess: isGetAllDatasSuccess,
            error: getAllDatasError,
            data: getAllDatasResponse
        }
    ] = useGetAllDatasMutation();

    const [
        updateDatalistItem,
        {
            isLoading: isUpdateDatalistItemLoading,
            isError: isUpdateDatalistItemError,
            isSuccess: isUpdateDatalistItemSuccess,
            error: updateDatalistItemError,
            data: updateDatalistItemResponse
        }
    ] = useUpdateSpecificDataMutation();
    const [
        deleteDatalistItem,
        {
            isLoading: isDeleteDatalistItemLoading,
            isError: isDeleteDatalistItemError,
            isSuccess: isDeleteDatalistItemSuccess,
            error: deleteDatalistItemError,
            data: deleteDatalistItemResponse
        }
    ] = useDeleteSpecificDataMutation();
    const [
        createDatalistItem,
        {
            isLoading: isCreateDatalistItemLoading,
            isError: isCreateDatalistItemError,
            isSuccess: isCreateDatalistItemSuccess,
            error: createDatalistItemError,
            data: createDatalistItemResponse
        }
    ] = useCreateSpecificDataMutation();

    useEffect(() => {
        if (isGetAllDatasLoading) return;
        if (isGetAllDatasError && getAllDatasError) {
            showErrorToast(getAllDatasError, {
                message: 'Erreur de chargement des listes'
            });
            return;
        }
        if (isGetAllDatasSuccess && getAllDatasResponse) {
            dispatch(setDatalists(getAllDatasResponse.data));
        }
    }, [isGetAllDatasLoading, isGetAllDatasError, isGetAllDatasSuccess, getAllDatasError, getAllDatasResponse, dispatch]);

    useEffect(() => {
        if (isUpdateDatalistItemLoading) return;
        if (isUpdateDatalistItemError && updateDatalistItemError) {
            showErrorToast(updateDatalistItemError, {
                message: `Erreur de mise à jour de l'élément "${lastUpdatedItemLabel}"`
            });
        }
        if (isUpdateDatalistItemSuccess && updateDatalistItemResponse) {
            showApiToast(updateDatalistItemResponse, {
                successMessage: `Élément "${lastUpdatedItemLabel}" mis à jour avec succès`,
                errorMessage: `Erreur lors de la mise à jour de l'élément "${lastUpdatedItemLabel}"`
            });
        }
    }, [isUpdateDatalistItemLoading, isUpdateDatalistItemError, updateDatalistItemError, updateDatalistItemResponse]);

    useEffect(() => {
        if (isDeleteDatalistItemLoading) return;
        if (isDeleteDatalistItemError && deleteDatalistItemError) {
            showErrorToast(deleteDatalistItemError, {
                message: `Erreur de suppression de l'élément "${lastDeletedItemLabel}"`
            });
        }
        if (isDeleteDatalistItemSuccess && deleteDatalistItemResponse) {
            showApiToast(deleteDatalistItemResponse, {
                successMessage: `Élément "${lastDeletedItemLabel}" supprimé avec succès`,
                errorMessage: `Erreur lors de la suppression de l'élément "${lastDeletedItemLabel}"`
            });
        }
    }, [isDeleteDatalistItemLoading, isDeleteDatalistItemError, deleteDatalistItemError, deleteDatalistItemResponse]);

    useEffect(() => {
        if (isCreateDatalistItemLoading) return;
        if (isCreateDatalistItemError && createDatalistItemError) {
            showErrorToast(createDatalistItemError, {
                message: `Erreur de création de l'élément "${lastCreatedItemLabel}"`
            });
        }
        if (isCreateDatalistItemSuccess && createDatalistItemResponse) {
            showApiToast(createDatalistItemResponse, {
                successMessage: `Élément "${lastCreatedItemLabel}" créé avec succès`,
                errorMessage: `Erreur lors de la création de l'élément "${lastCreatedItemLabel}"`
            });
        }
    }, [isCreateDatalistItemLoading, isCreateDatalistItemError, createDatalistItemError, createDatalistItemResponse]);

    const handleEditItem = (item: { id: number; label: string }) => {
        setDataListItemToEdit(item);
        setModalMode('edit');
        setLastUpdatedItemLabel(item.label);
        setIsModalOpen(true);
    };

    const handleDeleteItem = (item: { id: number; label: string }) => {
        setDataListItemToDelete(item);
        setModalMode('delete');
        setLastDeletedItemLabel(item.label);
        setIsModalOpen(true);
    };

    const handleUpdateItem = async (newLabel: string) => {
        if (!dataListItemToEdit) return;

        try {
            setLastUpdatedItemLabel(newLabel);
            await updateDatalistItem({
                id: dataListItemToEdit.id,
                value: newLabel
            });

            setIsModalOpen(false);
            setDataListItemToEdit(null);
            await getAllDatas(undefined);
        } catch (error) {
            console.error('Erreur lors de la mise à jour:', error);
        }
    };

    const handleDeleteItemConfirm = async () => {
        if (!dataListItemToDelete) return;

        try {
            setLastDeletedItemLabel(dataListItemToDelete.label);
            await deleteDatalistItem({
                id: dataListItemToDelete.id
            });

            setIsModalOpen(false);
            setDataListItemToDelete(null);
            await getAllDatas(undefined);
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
        }
    };

    const handleAddItem = (listType: string) => {
        setCurrentListType(listType);
        setModalMode('create');
        setIsModalOpen(true);
    };

    const handleCreateItem = async (label: string) => {
        if (!currentListType) return;

        try {
            setLastCreatedItemLabel(label);
            await createDatalistItem({
                key: currentListType,
                value: label
            });

            setIsModalOpen(false);
            await getAllDatas(undefined);
        } catch (error) {
            console.error('Erreur lors de la création:', error);
        }
    };

    const handleModalTitle = () => {
        switch (modalMode) {
            case 'edit':
                return "Modifier l'élément";
            case 'delete':
                return "Supprimer l'élément";
            case 'create':
                return 'Ajouter un élément';
            default:
                return '';
        }
    };

    const handleModalYesText = () => {
        switch (modalMode) {
            case 'edit':
                return 'Modifier';
            case 'delete':
                return 'Supprimer';
            case 'create':
                return 'Ajouter';
            default:
                return '';
        }
    };

    return {
        datalists,
        dataListItemToEdit,
        isModalOpen,
        setIsModalOpen,
        modalMode,
        handleEditItem,
        handleDeleteItem,
        handleUpdateItem,
        handleDeleteItemConfirm,
        handleModalTitle,
        handleModalYesText,
        handleAddItem,
        handleCreateItem
    };
};
